import { useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, GripVertical, Plus, TriangleAlert, X } from "lucide-react";

import {
  STEP_KINDS,
  newStep,
  showValue,
  stepSummary,
  type Step,
  type StepKind,
} from "@/lib/integrations/settings";

import { Button, Field, FieldRow, Input, Select } from "./controls";
import styles from "./integrations.module.css";
import { Floating } from "./Floating";
import { MenuItem, MenuLabel } from "./Popover";

/**
 * The steps on a field, in order. Drag a chip to move it, or use the arrows in
 * its editor; click a chip to change it.
 */
export function StepChips({
  steps,
  onChange,
  renderEditor,
  warnings = {},
  disabled = false,
  open,
  onOpen,
}: {
  steps: Step[];
  onChange: (steps: Step[]) => void;
  /** The editor for one step, opened from its chip. */
  renderEditor: (index: number, close: () => void) => ReactNode;
  /** A step index → why it needs a look (e.g. "2 new"). */
  warnings?: Record<number, string>;
  disabled?: boolean;
  /** Which step's editor is open, when the caller wants to open one itself. */
  open?: number | null;
  onOpen?: (index: number | null) => void;
}) {
  const [ownEditing, setOwnEditing] = useState<number | null>(null);
  const editing = open !== undefined ? open : ownEditing;
  const setEditing = (index: number | null) => {
    onOpen?.(index);
    if (open === undefined) setOwnEditing(index);
  };
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= steps.length || from === to) return;
    const next = [...steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className={styles.chips}>
      {steps.length === 0 && disabled && <span className={styles.faint}>—</span>}
      {steps.map((step, index) => (
        <Floating
          key={`${index}-${step.kind}`}
          open={editing === index}
          onClose={() => setEditing(null)}
          wide
          trigger={
            <span
              className={[
                styles.stepChip,
                warnings[index] ? styles.stepChipWarn : "",
                dragging === index ? styles.stepChipDragging : "",
              ]
                .filter(Boolean)
                .join(" ")}
              draggable={!disabled}
              onDragStart={(event) => {
                setDragging(index);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(index));
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const from = Number(event.dataTransfer.getData("text/plain"));
                if (Number.isFinite(from)) move(from, index);
                setDragging(null);
              }}
            >
              <button
                type="button"
                className={styles.stepChipMain}
                onClick={() => setEditing(editing === index ? null : index)}
                title={warnings[index] ? `${stepSummary(step)} — ${warnings[index]}` : stepSummary(step)}
              >
                {!disabled && (
                  <span className={styles.grip} aria-hidden>
                    <GripVertical size={12} />
                  </span>
                )}
                {stepSummary(step)}
                {warnings[index] && (
                  <span className={styles.warnText}>
                    {" "}
                    · {warnings[index]} <TriangleAlert size={12} aria-hidden />
                  </span>
                )}
              </button>
              {!disabled && (
                <button
                  type="button"
                  className={styles.stepChipX}
                  aria-label={`Remove the step ${stepSummary(step)}`}
                  onClick={() => onChange(steps.filter((_, i) => i !== index))}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          }
        >
          {editing === index && (
            <div>
              {renderEditor(index, () => setEditing(null))}
              {steps.length > 1 && !disabled && (
                <div className={styles.actions} style={{ marginTop: "var(--s2)" }}>
                  <Button tone="subtle" disabled={index === 0} onClick={() => { move(index, index - 1); setEditing(index - 1); }}>
                    <ArrowLeft size={14} aria-hidden /> Earlier
                  </Button>
                  <Button tone="subtle" disabled={index === steps.length - 1} onClick={() => { move(index, index + 1); setEditing(index + 1); }}>
                    Later <ArrowRight size={14} aria-hidden />
                  </Button>
                </div>
              )}
            </div>
          )}
        </Floating>
      ))}
      {!disabled && (
        <Floating
          open={adding}
          onClose={() => setAdding(false)}
          trigger={
            <button type="button" className={styles.addStep} onClick={() => setAdding(!adding)} aria-label="Add a step">
              <Plus size={12} aria-hidden />
              Step
            </button>
          }
        >
          <MenuLabel>Add a step</MenuLabel>
          {STEP_KINDS.map((one) => (
            <MenuItem
              key={one.kind}
              label={one.label}
              onClick={() => {
                setAdding(false);
                onChange([...steps, newStep(one.kind)]);
                setEditing(steps.length);
              }}
            />
          ))}
        </Floating>
      )}
    </div>
  );
}

export type StepPreview = {
  rows: { before: unknown; after: unknown; problem: string | null }[];
  loading: boolean;
  error?: string | null;
};

/** One step: pick its kind and options, and see it on five real rows. */
export function StepEditor({
  step,
  onChange,
  onClose,
  preview,
  children,
  disabled = false,
}: {
  step: Step;
  onChange: (step: Step) => void;
  onClose: () => void;
  /** The field on five rows: what Scout sent, and the value after the steps up to this one. */
  preview: StepPreview;
  /** Extra settings for a kind, such as the value map. */
  children?: ReactNode;
  disabled?: boolean;
}) {
  const set = (patch: Partial<Step>) => onChange({ ...step, ...patch });
  const text = (value: unknown) => (value === null || value === undefined ? "" : String(value));
  return (
    <div className={step.kind === "map" ? `${styles.editor} ${styles.editorWide}` : styles.editor}>
      <div className={styles.editorHead}>
        <span>Step</span>
        <Button tone="subtle" icon aria-label="Close" onClick={onClose}>
          <X size={15} />
        </Button>
      </div>
      <Field label="What it does" hint={STEP_KINDS.find((one) => one.kind === step.kind)?.note}>
        <Select value={step.kind} disabled={disabled} onChange={(event) => onChange(newStep(event.target.value as StepKind))}>
          {STEP_KINDS.map((one) => (
            <option key={one.kind} value={one.kind}>
              {one.label}
            </option>
          ))}
        </Select>
      </Field>

      {step.kind === "strip" && (
        <FieldRow>
          <Field label="Characters to remove">
            <Input value={step.chars ?? ""} disabled={disabled} onChange={(event) => set({ chars: event.target.value })} />
          </Field>
          <Field label="Where">
            <Select value={step.at ?? "both"} disabled={disabled} onChange={(event) => set({ at: event.target.value as Step["at"] })}>
              <option value="start">At the start</option>
              <option value="end">At the end</option>
              <option value="both">At both ends</option>
            </Select>
          </Field>
        </FieldRow>
      )}
      {step.kind === "replace" && (
        <FieldRow>
          <Field label="Find">
            <Input value={step.find ?? ""} disabled={disabled} onChange={(event) => set({ find: event.target.value })} />
          </Field>
          <Field label="Replace with">
            <Input value={step.replaceWith ?? ""} disabled={disabled} onChange={(event) => set({ replaceWith: event.target.value })} />
          </Field>
        </FieldRow>
      )}
      {step.kind === "case" && (
        <Field label="Case">
          <Select value={step.case ?? "upper"} disabled={disabled} onChange={(event) => set({ case: event.target.value as Step["case"] })}>
            <option value="upper">UPPER</option>
            <option value="lower">lower</option>
            <option value="title">Title</option>
          </Select>
        </Field>
      )}
      {step.kind === "part" && (
        <FieldRow>
          <Field label="Split by">
            <Input value={step.separator ?? " "} disabled={disabled} onChange={(event) => set({ separator: event.target.value })} />
          </Field>
          <Field label="Keep">
            <Select value={step.which ?? "first"} disabled={disabled} onChange={(event) => set({ which: event.target.value as Step["which"] })}>
              <option value="first">The first part</option>
              <option value="last">The last part</option>
              <option value="nth">Part number…</option>
            </Select>
          </Field>
          {step.which === "nth" && (
            <Field label="Part number">
              <Input
                type="number"
                min={1}
                value={(step.index ?? 0) + 1}
                disabled={disabled}
                onChange={(event) => set({ index: Math.max(0, Number(event.target.value) - 1) })}
              />
            </Field>
          )}
        </FieldRow>
      )}
      {step.kind === "datetime" && (
        <Field label="Format" hint="Leave empty for 2026-09-23T07:30. Otherwise e.g. %d/%m/%Y %H:%M. With no time zone, the event's is used.">
          <Input value={step.format ?? ""} disabled={disabled} placeholder="%d/%m/%Y %H:%M" onChange={(event) => set({ format: event.target.value || null })} />
        </Field>
      )}
      {step.kind === "default" && (
        <Field label="Value when empty">
          <Input value={text(step.default)} disabled={disabled} onChange={(event) => set({ default: event.target.value })} />
        </Field>
      )}
      {step.kind === "lookup" && (
        <FieldRow>
          <Field label="Kind of record">
            <Select value={step.entity ?? "sport"} disabled={disabled} onChange={(event) => set({ entity: event.target.value as Step["entity"] })}>
              <option value="sport">Sport</option>
              <option value="participant">Player or team</option>
              <option value="venue">Venue</option>
              <option value="fixture">Fixture</option>
            </Select>
          </Field>
          <Field label="Whose ids" hint="The id links source, e.g. ag2026.">
            <Input value={step.idSource ?? ""} disabled={disabled} onChange={(event) => set({ idSource: event.target.value || null })} />
          </Field>
        </FieldRow>
      )}
      {step.kind === "join" && (
        <Field label="Separator">
          <Input value={step.separator ?? " "} disabled={disabled} onChange={(event) => set({ separator: event.target.value })} />
        </Field>
      )}
      {step.kind === "pick" && (
        <FieldRow>
          <Field label="Which">
            <Select value={step.which === "last" ? "last" : "first"} disabled={disabled} onChange={(event) => set({ which: event.target.value as Step["which"] })}>
              <option value="first">The first</option>
              <option value="last">The last</option>
            </Select>
          </Field>
          <Field label="Where column (optional)">
            <Input value={step.column ?? ""} disabled={disabled} onChange={(event) => set({ column: event.target.value || null })} />
          </Field>
          <Field label="Equals">
            <Input value={text(step.equals)} disabled={disabled} onChange={(event) => set({ equals: event.target.value })} />
          </Field>
          <Field label="Read column (optional)">
            <Input value={step.read ?? ""} disabled={disabled} onChange={(event) => set({ read: event.target.value || null })} />
          </Field>
        </FieldRow>
      )}
      {step.kind === "round" && (
        <Field label="Decimals">
          <Input
            type="number"
            min={0}
            max={6}
            value={step.decimals ?? 0}
            disabled={disabled}
            onChange={(event) => set({ decimals: Math.max(0, Math.min(6, Number(event.target.value) || 0)) })}
          />
        </Field>
      )}

      {children}

      <div>
        <div className={styles.muted} style={{ marginBottom: "var(--s1)" }}>
          On five real rows {preview.loading && "· reading…"}
        </div>
        {preview.error ? (
          <p className={styles.badText}>{preview.error}</p>
        ) : (
          <table className={styles.miniTable}>
            <thead>
              <tr>
                <th>Scout sent</th>
                <th>After this step</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.length === 0 && (
                <tr>
                  <td colSpan={2} className={styles.faint}>
                    {preview.loading ? "Reading…" : "No rows to show."}
                  </td>
                </tr>
              )}
              {preview.rows.map((row, index) => (
                <tr key={index}>
                  <td className={styles.mono}>{showValue(row.before)}</td>
                  <td className={row.problem ? styles.badText : styles.mono}>
                    {row.problem ? (
                      <>
                        <TriangleAlert size={12} aria-hidden /> {row.problem}
                      </>
                    ) : (
                      showValue(row.after)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
